import api from './api';
import type {
  AttendanceRecord,
  AttendanceUpdateData,
  AttendanceQuery,
  MonthlyStats,
  TeamMemberStatus,
  UnfinishedAttendance
} from '../types/attendance';

export const attendanceService = {
  // 勤怠記録一覧取得
  async getAttendanceRecords(query?: AttendanceQuery): Promise<AttendanceRecord[]> {
    const params = new URLSearchParams();

    if (query?.user_id) {
      params.append('user_id', query.user_id.toString());
    }
    if (query?.date_from) {
      params.append('date_from', query.date_from);
    }
    if (query?.date_to) {
      params.append('date_to', query.date_to);
    }

    // キャッシュを避けるためにタイムスタンプを追加
    params.append('_t', Date.now().toString());

    const response = await api.get<AttendanceRecord[]>(`/attendance?${params.toString()}`);
    return response.data;
  },

  // 特定の勤怠記録取得
  async getAttendanceRecord(id: number): Promise<AttendanceRecord> {
    const response = await api.get<AttendanceRecord>(`/attendance/${id}`);
    return response.data;
  },

  // 出勤打刻
  async clockIn(): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>('/attendance/clock-in', {});
    return response.data;
  },

  // 退勤打刻
  async clockOut(id: number): Promise<AttendanceRecord> {
    const response = await api.put<AttendanceRecord>(`/attendance/${id}/clock-out`);
    return response.data;
  },

  // 勤怠記録更新
  async updateAttendanceRecord(id: number, data: AttendanceUpdateData): Promise<AttendanceRecord> {
    const response = await api.put<AttendanceRecord>(`/attendance/${id}`, data);
    return response.data;
  },

  // 勤怠記録削除
  async deleteAttendanceRecord(id: number): Promise<{ message: string; id: number }> {
    const response = await api.delete<{ message: string; id: number }>(`/attendance/${id}`);
    return response.data;
  },

  // 今日の勤怠記録取得（ユーザー用）
  async getTodayAttendance(userId: number): Promise<AttendanceRecord | null> {
    // JST基準で今日の日付を取得
    const jstDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
    const year = jstDate.getFullYear();
    const month = String(jstDate.getMonth() + 1).padStart(2, '0');
    const day = String(jstDate.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;

    // 昨日の日付も計算（日付跨ぎ退勤対応）
    const yesterdayDate = new Date(jstDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = `${yesterdayDate.getFullYear()}-${String(yesterdayDate.getMonth() + 1).padStart(2, '0')}-${String(yesterdayDate.getDate()).padStart(2, '0')}`;

    // 昨日〜今日の記録を取得（日付跨ぎ退勤に対応）
    const records = await this.getAttendanceRecords({
      user_id: userId,
      date_from: yesterday,
      date_to: today
    });

    // 空のレコード（id が null）を除外
    const validRecords = records.filter(record => record.id !== null);

    // 今日に関連する記録のみをフィルタリング
    // - dateが今日の記録
    // - または、clock_outが今日の記録（前日出勤→今日退勤のケース）
    const todayRecords = validRecords.filter(record => {
      const recordDate = String(record.date).split('T')[0];

      const clockOutDate = record.clock_out
        ? String(record.clock_out).split('T')[0]
        : null;

      return recordDate === today || clockOutDate === today;
    });

    // 記録がない場合は null を返す
    if (todayRecords.length === 0) return null;

    // 複数の記録がある場合は最新のものを返す（created_atで降順ソート）
    const sortedRecords = todayRecords.sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return sortedRecords[0];
  },

  // 現在の勤怠記録取得（今日の記録を優先、なければ未退勤の記録）
  async getCurrentAttendance(userId: number): Promise<AttendanceRecord | null> {
    // まず今日の記録をチェック（優先）
    const todayRecord = await this.getTodayAttendance(userId);
    console.log('[getCurrentAttendance] todayRecord:', todayRecord);

    if (todayRecord) {
      console.log('[getCurrentAttendance] 今日の記録を返します');
      return todayRecord;
    }

    // 今日の記録がなければ未退勤の記録をチェック（前日からの持ち越し対応）
    console.log('[getCurrentAttendance] 未退勤記録をチェック中...');
    const unfinished = await this.checkUnfinished();
    console.log('[getCurrentAttendance] unfinished:', unfinished);

    if (unfinished.hasUnfinished && unfinished.attendance) {
      console.log('[getCurrentAttendance] 未退勤記録を返します:', unfinished.attendance);
      return unfinished.attendance as AttendanceRecord;
    }

    console.log('[getCurrentAttendance] 記録なし、nullを返します');
    return null;
  },

  // 月次勤怠統計取得
  async getMonthlyStats(): Promise<MonthlyStats> {
    const response = await api.get<MonthlyStats>('/attendance/stats/monthly');
    return response.data;
  },

  // チーム勤怠状況取得（manager以上）
  async getTeamStatus(): Promise<TeamMemberStatus[]> {
    const response = await api.get<TeamMemberStatus[]>('/attendance/stats/team');
    return response.data;
  },

  // 未退勤の勤怠記録チェック
  async checkUnfinished(): Promise<UnfinishedAttendance> {
    // キャッシュを避けるためにタイムスタンプを追加
    const response = await api.get<UnfinishedAttendance>(`/attendance/unfinished?_t=${Date.now()}`);
    return response.data;
  },

  // 休憩開始
  async startBreak(attendanceId: number): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(`/attendance/${attendanceId}/break-start`);
    return response.data;
  },

  // 休憩終了
  async endBreak(attendanceId: number): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(`/attendance/${attendanceId}/break-end`);
    return response.data;
  },
};